import { cn } from "@istari-frontend/shared";
import { Badge, ScrollArea, Toggle } from "@istari-frontend/ui";
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleSlash,
  Clock,
  Filter,
  FlaskConical,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";

import { JUnitTestCase, JUnitTestSuite } from "./parse-junit-xml";

const EARLIEST_VALID_YEAR = 2000;
const FUTURE_TOLERANCE_MS = 24 * 60 * 60 * 1000;

function parseTimestamp(value: string | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return null;
  if (d.getUTCFullYear() < EARLIEST_VALID_YEAR) return null;
  if (ms > Date.now() + FUTURE_TOLERANCE_MS) return null;
  return d;
}

function isFailedCase(tc: JUnitTestCase): boolean {
  return tc.status === "failed" || tc.status === "error";
}

export function JUnitReportView({ suites }: { suites: JUnitTestSuite[] }) {
  const [failuresOnly, setFailuresOnly] = useState(false);

  const totals = useMemo(() => {
    let tests = 0;
    let passed = 0;
    let failures = 0;
    let errors = 0;
    let skipped = 0;
    let time: number | null = null;
    let runDate: Date | null = null;

    for (const s of suites) {
      tests += s.tests;
      passed += s.testCases.filter((tc) => tc.status === "passed").length;
      failures += s.failures;
      errors += s.errors;
      skipped += s.skipped;
      if (s.time != null) time = (time ?? 0) + s.time;
      const d = parseTimestamp(s.timestamp);
      if (d && (runDate === null || d < runDate)) {
        runDate = d;
      }
    }

    return { tests, passed, failures, errors, skipped, time, runDate };
  }, [suites]);

  const hasFailures = totals.failures > 0 || totals.errors > 0;
  const hasMixedResults = hasFailures && totals.passed > 0;

  const visibleSuites = useMemo(() => {
    if (!failuresOnly) return suites;
    return suites.reduce<JUnitTestSuite[]>((acc, suite) => {
      const failed = suite.testCases.filter(isFailedCase);
      if (failed.length > 0) acc.push({ ...suite, testCases: failed });
      return acc;
    }, []);
  }, [suites, failuresOnly]);

  return (
    <ScrollArea className="h-full">
      <div className="mx-auto max-w-4xl space-y-4 p-4 pb-16">
        <div
          className={cn(
            "flex flex-wrap items-center gap-3 rounded-lg border p-4",
            hasFailures
              ? "border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/30"
              : "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30",
          )}
        >
          <div className="flex items-center gap-2">
            {hasFailures ? (
              <XCircle className="size-5 text-red-600 dark:text-red-400" />
            ) : (
              <CheckCircle2 className="size-5 text-green-600 dark:text-green-400" />
            )}
            <span className="text-foreground text-sm font-semibold">
              {hasFailures ? "Some tests failed" : "All tests passed"}
            </span>
          </div>
          <TestReportSummary summary={totals} />
          {hasMixedResults && (
            <Toggle
              variant="outline"
              size="sm"
              pressed={failuresOnly}
              onPressedChange={setFailuresOnly}
              className="ml-auto"
              aria-label="Show only failures and errors"
            >
              <Filter className="size-3.5" />
              <span className="text-xs">Failures only</span>
            </Toggle>
          )}
        </div>

        {visibleSuites.map((suite, i) => (
          <TestSuiteSection
            key={`${suite.name}-${i}`}
            suite={suite}
            defaultOpen={visibleSuites.length <= 5 || failuresOnly}
          />
        ))}
        {failuresOnly && visibleSuites.length === 0 && (
          <div className="text-muted-foreground py-8 text-center text-sm">No failures or errors to display</div>
        )}
      </div>
    </ScrollArea>
  );
}

function TestSuiteSection({ suite, defaultOpen }: { suite: JUnitTestSuite; defaultOpen: boolean }) {
  const hasFailures = suite.failures > 0 || suite.errors > 0;
  const [open, setOpen] = useState(defaultOpen || hasFailures);
  const passed = suite.testCases.filter((tc) => tc.status === "passed").length;

  return (
    <div className="bg-card rounded-lg border">
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-2 px-4 py-3 text-left",
          "hover:bg-accent/50 transition-colors",
          open && "border-b",
        )}
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? (
          <ChevronDown className="text-muted-foreground size-4 shrink-0" />
        ) : (
          <ChevronRight className="text-muted-foreground size-4 shrink-0" />
        )}
        <FlaskConical className="text-muted-foreground size-4 shrink-0" />
        <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">{suite.name}</span>
        <div className="flex shrink-0 items-center gap-2">
          {passed > 0 && (
            <Badge variant="success" size="xs">
              {passed}
            </Badge>
          )}
          {suite.failures > 0 && (
            <Badge variant="destructive" size="xs">
              {suite.failures}
            </Badge>
          )}
          {suite.errors > 0 && (
            <Badge variant="destructive" size="xs">
              {suite.errors}
            </Badge>
          )}
          {suite.skipped > 0 && (
            <Badge variant="warning" size="xs">
              {suite.skipped}
            </Badge>
          )}
          {suite.time != null && (
            <span className="text-muted-foreground flex items-center gap-1 text-xs">
              <Clock className="size-3" />
              {formatDuration(suite.time)}
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="divide-y">
          {suite.testCases.map((tc, i) => (
            <TestCaseRow key={`${tc.classname}.${tc.name}-${i}`} testCase={tc} />
          ))}
          {suite.testCases.length === 0 && (
            <div className="text-muted-foreground px-4 py-6 text-center text-sm">No test cases in this suite</div>
          )}
        </div>
      )}
    </div>
  );
}

function TestCaseRow({ testCase }: { testCase: JUnitTestCase }) {
  const hasDetails = !!(testCase.message || testCase.details || testCase.systemOut || testCase.systemErr);
  const isFailure = isFailedCase(testCase);
  const [expanded, setExpanded] = useState(isFailure);

  return (
    <div>
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-2 px-4 py-2 text-left",
          hasDetails && "hover:bg-accent/50 cursor-pointer transition-colors",
          !hasDetails && "cursor-default",
        )}
        onClick={() => hasDetails && setExpanded((prev) => !prev)}
      >
        <StatusIcon status={testCase.status} />
        <span className="text-foreground min-w-0 flex-1 truncate text-sm">{testCase.name}</span>
        {testCase.time != null && (
          <span className="text-muted-foreground shrink-0 text-xs">{formatDuration(testCase.time)}</span>
        )}
        {hasDetails &&
          (expanded ? (
            <ChevronDown className="text-muted-foreground size-3.5 shrink-0" />
          ) : (
            <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
          ))}
      </button>
      {expanded && hasDetails && (
        <div className="bg-muted/30 space-y-2 border-t px-4 py-3">
          {testCase.message && (
            <p className={cn("text-xs font-medium", isFailure ? "text-destructive" : "text-muted-foreground")}>
              {testCase.type && <span className="text-muted-foreground font-normal">{testCase.type}: </span>}
              {testCase.message}
            </p>
          )}
          {testCase.details && (
            <pre className="text-muted-foreground overflow-x-auto font-mono text-xs leading-relaxed break-words whitespace-pre-wrap">
              {testCase.details}
            </pre>
          )}
          {testCase.systemOut && (
            <div>
              <p className="text-muted-foreground mb-0.5 text-[0.6875rem] font-medium tracking-wide uppercase">
                stdout
              </p>
              <pre className="text-muted-foreground overflow-x-auto font-mono text-xs leading-relaxed break-words whitespace-pre-wrap">
                {testCase.systemOut}
              </pre>
            </div>
          )}
          {testCase.systemErr && (
            <div>
              <p className="text-muted-foreground mb-0.5 text-[0.6875rem] font-medium tracking-wide uppercase">
                stderr
              </p>
              <pre className="text-muted-foreground overflow-x-auto font-mono text-xs leading-relaxed break-words whitespace-pre-wrap">
                {testCase.systemErr}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface TestReportTotals {
  tests: number;
  passed: number;
  failures: number;
  errors: number;
  skipped: number;
  time: number | null;
  runDate: Date | null;
}

function TestReportSummary({ summary }: { summary: TestReportTotals }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="success" size="sm">
        {summary.passed} passed
      </Badge>
      {summary.failures > 0 && (
        <Badge variant="destructive" size="sm">
          {summary.failures} failed
        </Badge>
      )}
      {summary.errors > 0 && (
        <Badge variant="destructive" size="sm">
          {summary.errors} error{summary.errors !== 1 ? "s" : ""}
        </Badge>
      )}
      {summary.skipped > 0 && (
        <Badge variant="warning" size="sm">
          {summary.skipped} skipped
        </Badge>
      )}
      <span className="text-muted-foreground text-xs">
        {summary.tests} total
        {summary.time != null && <> &middot; {formatDuration(summary.time)}</>}
      </span>
      {summary.runDate && (
        <span className="text-muted-foreground flex items-center gap-1 text-xs" data-testid="junit-run-date">
          <Calendar className="size-3" />
          {formatRunDate(summary.runDate)}
        </span>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: JUnitTestCase["status"] }) {
  switch (status) {
    case "passed":
      return <CheckCircle2 className="size-4 shrink-0 text-green-600 dark:text-green-400" />;
    case "failed":
      return <XCircle className="size-4 shrink-0 text-red-600 dark:text-red-400" />;
    case "error":
      return <AlertTriangle className="size-4 shrink-0 text-red-600 dark:text-red-400" />;
    case "skipped":
      return <CircleSlash className="size-4 shrink-0 text-amber-500 dark:text-amber-400" />;
  }
}

function formatRunDate(date: Date): string {
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 0.001) return "<1ms";
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(2)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs.toFixed(1)}s`;
}
